import { groupBy, uniq } from 'es-toolkit';
import { outputReport, getOutputFormat } from '@/utils/reportOutput';
import { entityService } from '@/services/EntityService';
import { iemService, type IEItem, type PHRItem, type THRItem } from '@/services/iem';
import type {
  ReceiptFilter,
  RecoverableFilter,
  CatalogType,
} from '@/stores/frontWindowPreferencesStore';
import { formatCatalogType } from '@/components/iem/tabs/allocationPrintUtils';
import { getPersonLabels } from '@/hooks/usePersonLabels';
import {
  buildAllocationSummaryReportHtml,
  type AllocationSummaryMemberInfo,
  type AllocationSummaryItem,
  type AllocationSummaryTemplateOptions,
} from '@/reports/templates/AllocationSummaryTemplate';
import { multiWordMatch } from '@/utils/multiWordMatch';

type AllocationSummaryRowSource = 'phr' | 'thr';

interface AllocationSummaryRow {
  source: AllocationSummaryRowSource;
  item: string;
  lin: string;
  nsn: string;
  catalogId: string | null;
  handReceipt: 'PHR' | 'THR' | 'PnP';
  serialNumber: string | null;
  quantity: number;
  status: string | null;
  issueDate: string | null;
  recoverable: boolean | null;
  catalogType: string;
  unauthorized: boolean;
}

const normalizeIssueDate = (value: string | null | undefined): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  if (!trimmed || trimmed === '--' || trimmed === '—') {
    return null;
  }
  return trimmed;
};

const normalizeReceiptFilter = (value: unknown): ReceiptFilter => {
  if (value === 'phr' || value === 'thr' || value === 'all') {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'phr' || normalized === 'thr' || normalized === 'all') {
      return normalized as ReceiptFilter;
    }
  }
  return 'all';
};

const normalizeRecoverableFilter = (value: unknown): RecoverableFilter => {
  if (value === 'recoverable' || value === 'nonRecoverable' || value === 'all') {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'recoverable') {
      return 'recoverable';
    }
    if (normalized === 'nonrecoverable' || normalized === 'non-recoverable') {
      return 'nonRecoverable';
    }
    if (normalized === 'all') {
      return 'all';
    }
  }
  return 'all';
};

const normalizeSearch = (value: unknown): string => (typeof value === 'string' ? value : '');

const resolveMemberInfo = async (personId: string): Promise<AllocationSummaryMemberInfo> => {
  const person = await entityService.get({ type: 'person', id: personId });
  if (!person) {
    return { name: 'Unknown' };
  }

  const entityData = (person.entity_data ?? {}) as Record<string, unknown>;
  const metadata = (person.metadata ?? {}) as Record<string, unknown>;

  const rank =
    typeof entityData.rank === 'string'
      ? entityData.rank
      : typeof metadata.rank === 'string'
        ? metadata.rank
        : null;

  const unit =
    typeof entityData.unit === 'string'
      ? entityData.unit
      : typeof metadata.unit === 'string'
        ? metadata.unit
        : null;

  const individualId =
    typeof entityData.individual_id === 'string'
      ? entityData.individual_id
      : typeof metadata.individual_id === 'string'
        ? metadata.individual_id
        : null;

  const firstName = typeof entityData.first_name === 'string' ? entityData.first_name.trim() : '';
  const lastName = typeof entityData.last_name === 'string' ? entityData.last_name.trim() : '';
  // Build name from first/last to avoid duplicating rank already embedded in display_name
  const fullName = [firstName, lastName].filter(Boolean).join(' ');

  return {
    name: fullName || person.display_name || 'Unknown',
    rank: rank?.trim() || null,
    unit: unit?.trim() || null,
    individualId: individualId?.trim() || null,
  };
};

const buildAllocationSummaryItems = ({
  phrItems,
  thrItems,
  ieItems,
  receiptFilter,
  recoverableFilter,
  typeFilters,
  search,
}: {
  phrItems: PHRItem[];
  thrItems: THRItem[];
  ieItems: IEItem[];
  receiptFilter: ReceiptFilter;
  recoverableFilter: RecoverableFilter;
  typeFilters: CatalogType[];
  search: string;
}): AllocationSummaryItem[] => {
  const authorizedCatalogIdSet: Set<string> = new Set(
    ieItems
      .filter(
        (b) => {
          const normalizedStatus = (b.status ?? '').trim().toLowerCase();
          return (
            (b.catalogId ?? '') !== '' &&
            !normalizedStatus.startsWith('not authorized') &&
            !normalizedStatus.startsWith('unauthorized')
          );
        },
      )
      .map((b) => String(b.catalogId)) as string[],
  );

  const phrRows: AllocationSummaryRow[] = phrItems.map((item) => {
    const statusLower = (item.status ?? '').toLowerCase();
    const linkedToIe =
      (item.catalogId ? authorizedCatalogIdSet.has(item.catalogId) : false) ||
      (item.parentCatalogId ? authorizedCatalogIdSet.has(item.parentCatalogId) : false);
    const unauthorized =
      statusLower.includes('unauthorized') ||
      (!linkedToIe && ieItems.length > 0 && item.type !== 'pnp');

    return {
      source: 'phr',
      item: item.item,
      lin: item.lin ?? '',
      nsn: item.nsn,
      catalogId: item.catalogId ?? null,
      handReceipt: item.type === 'pnp' ? ('PnP' as const) : ('PHR' as const),
      serialNumber: item.serial_number ?? null,
      quantity: 1,
      status: item.status ?? null,
      issueDate: normalizeIssueDate(item.issueDate),
      recoverable: item.recoverable ?? null,
      catalogType: formatCatalogType(item.type),
      unauthorized,
    };
  });

  const thrRows: AllocationSummaryRow[] = thrItems.map((item) => {
    const statusLower = (item.status ?? '').toLowerCase();
    const linkedToIe =
      (item.catalogId ? authorizedCatalogIdSet.has(item.catalogId) : false) ||
      (item.parentCatalogId ? authorizedCatalogIdSet.has(item.parentCatalogId) : false);
    const unauthorized =
      statusLower.includes('unauthorized') ||
      (!linkedToIe && ieItems.length > 0 && item.type !== 'pnp');

    return {
      source: 'thr',
      item: item.item,
      lin: item.lin ?? '',
      nsn: item.nsn,
      catalogId: item.catalogId ?? null,
      handReceipt: 'THR',
      serialNumber: null,
      quantity: item.qty ?? 0,
      status: item.status ?? null,
      issueDate: normalizeIssueDate(item.issueDate),
      recoverable: item.recoverable ?? null,
      catalogType: formatCatalogType(item.type),
      unauthorized,
    };
  });

  // Keep THR items ahead of PHR items to match AllocationTab behavior.
  const combined = [...thrRows, ...phrRows];
  const searchValue = search.trim().toLowerCase();

  const filtered = combined.filter((row) => {
    if (receiptFilter === 'phr' && row.source !== 'phr') return false;
    if (receiptFilter === 'thr' && row.source !== 'thr') return false;
    if (
      typeFilters.length > 0 &&
      !typeFilters.includes(row.catalogType as CatalogType)
    )
      return false;
    if (recoverableFilter === 'recoverable' && row.recoverable !== true) return false;
    if (recoverableFilter === 'nonRecoverable' && row.recoverable !== false) return false;
    if ((row.status ?? '').toLowerCase() === 'shortage') return false;

    if (!searchValue) return true;
    const searchable = `${row.item} ${row.lin} ${row.nsn} ${row.serialNumber ?? ''} ${String(row.quantity)}`;
    return multiWordMatch(searchable, searchValue);
  });

  const grouped = groupBy(filtered, (row) => `${row.source}:${row.nsn}`);

  const merged = Object.entries(grouped).map(([, rows]) => {
    if (rows.length === 1) {
      return rows[0]!;
    }

    const first = rows[0]!;
    const totalQty = rows.reduce((sum, r) => sum + r.quantity, 0);

    const uniqueStatuses = uniq(rows.map((r) => r.status).filter(Boolean));
    const uniqueIssueDates = uniq(rows.map((r) => r.issueDate).filter(Boolean));

    return {
      ...first,
      quantity: totalQty,
      status: uniqueStatuses.length > 1 ? 'Multiple' : (uniqueStatuses[0] ?? 'Unknown'),
      issueDate: uniqueIssueDates.length > 1 ? 'Multiple' : (uniqueIssueDates[0] ?? null),
      unauthorized: rows.some((r) => r.unauthorized),
      source: first.source,
      nsn: first.nsn,
      lin: first.lin,
      item: first.item,
      catalogId: first.catalogId,
      handReceipt: first.handReceipt,
      serialNumber: first.serialNumber,
    } satisfies AllocationSummaryRow & { status: string };
  });

  return merged.map((row) => ({
    handReceipt: row.handReceipt,
    lin: row.lin,
    nsn: row.nsn,
    catalogId: row.catalogId,
    nomenclature: row.item,
    quantity: row.quantity,
    issueDate: row.issueDate,
  }));
};

const parseCatalogUnitCost = (
  entityData: Record<string, unknown> | null | undefined,
): number | null => {
  const raw = entityData?.unit_price;
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? raw : null;
  }
  if (typeof raw === 'string') {
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const chunk = <T>(values: T[], size: number): T[][] => {
  if (size <= 0) return [values];
  const chunks: T[][] = [];
  for (let i = 0; i < values.length; i += size) {
    chunks.push(values.slice(i, i + size));
  }
  return chunks;
};

const fetchCatalogUnitCosts = async (catalogIds: string[]): Promise<Map<string, number | null>> => {
  const uniqueIds = Array.from(
    new Set(
      catalogIds
        .map((id) => (typeof id === 'string' ? id.trim() : ''))
        .filter((id): id is string => Boolean(id && id.length > 0)),
    ),
  );

  const unitCostByCatalogId = new Map<string, number | null>();
  if (uniqueIds.length === 0) {
    return unitCostByCatalogId;
  }

  // Avoid overly long URLs (many UUIDs) by batching requests.
  const batches = chunk(uniqueIds, 75);
  for (const batchIds of batches) {
    const idFilter = batchIds.length === 1 ? `eq.${batchIds[0]}` : `in.(${batchIds.join(',')})`;

    const entities = await entityService.list({
      type: 'catalog',
      limit: batchIds.length,
      order: 'id.asc',
      filters: {
        id: idFilter,
        select: 'id,entity_data',
      },
    });

    for (const entity of entities) {
      unitCostByCatalogId.set(entity.id, parseCatalogUnitCost(entity.entity_data ?? null));
    }
  }

  return unitCostByCatalogId;
};

export const runAllocationSummaryReport = async (
  params: Record<string, unknown>,
): Promise<void> => {
  // Legacy mode: If called with pre-calculated 'items' and 'member' (AllocationTab originally did this)
  const passedItems = params.items as AllocationSummaryItem[] | undefined;
  const passedMember = params.member as AllocationSummaryMemberInfo | undefined;
  const passedHeading = params.heading as string | undefined;
  const passedFiltersSummary = params.filtersSummary as string | undefined;

  const receiptFilter = normalizeReceiptFilter(params.receiptFilter);
  const recoverableFilter = normalizeRecoverableFilter(params.recoverableFilter);
  const typeFilters = Array.isArray(params.typeFilters)
    ? (params.typeFilters as CatalogType[])
    : [];
  const search = normalizeSearch(params.search);

  let items: AllocationSummaryItem[] = [];
  let member: AllocationSummaryMemberInfo = { name: 'Unknown' };

  if (passedItems && passedMember) {
    // Mode 1: Pre-calculated data (legacy)
    items = passedItems;
    member = passedMember;
  } else {
    // Mode 2: Compute from PHR/THR/IE Kit (Standalone or Front Window)
    const personParam = params.person as { id: string; displayName: string } | null | undefined;
    const personId = personParam?.id ?? (params.personId as string | undefined)?.trim();
    if (!personId) {
      throw new Error(`${getPersonLabels(params).label} is required to run this report.`);
    }

    const phrItemsParam = Array.isArray(params.phrItems) ? (params.phrItems as PHRItem[]) : null;
    const thrItemsParam = Array.isArray(params.thrItems) ? (params.thrItems as THRItem[]) : null;
    const ieItemsParam = Array.isArray(params.ieItems) ? (params.ieItems as IEItem[]) : null;

    const [phrItems, thrItems, ieItems, resolvedMember] = await Promise.all([
      phrItemsParam ?? iemService.getPHRItems(personId, { status: 'neq.closed' }),
      thrItemsParam ?? iemService.getTHRItems(personId, { status: 'neq.closed' }),
      ieItemsParam ?? iemService.getIESummary(personId),
      passedMember ?? resolveMemberInfo(personId),
    ]);

    member = resolvedMember;
    items = buildAllocationSummaryItems({
      phrItems,
      thrItems,
      ieItems,
      receiptFilter,
      recoverableFilter,
      typeFilters,
      search,
    });
  }

  const catalogIds = items
    .map((item) => item.catalogId)
    .filter((id): id is string => typeof id === 'string' && id.trim().length > 0);
  const unitCostByCatalogId = await fetchCatalogUnitCosts(catalogIds);

  items = items.map((item) => {
    const hasUnitCost = typeof item.unitCost === 'number' && Number.isFinite(item.unitCost);
    if (hasUnitCost) {
      return item;
    }

    const catalogId = typeof item.catalogId === 'string' ? item.catalogId.trim() : '';
    const unitCost = catalogId ? (unitCostByCatalogId.get(catalogId) ?? null) : null;
    return { ...item, unitCost };
  });

  // Calculate counts
  let phrCount = 0;
  let thrCount = 0;

  items.forEach((item) => {
    if (item.handReceipt === 'PHR' || item.handReceipt === 'PnP') {
      phrCount += 1;
    } else {
      thrCount += 1;
    }
  });

  const options: AllocationSummaryTemplateOptions = {
    personLabels: getPersonLabels(params),
    generatedAt: new Date(),
    member,
    items,
    counts: {
      total: items.length,
      phrCount,
      thrCount,
    },
    heading: passedHeading,
    filtersSummary: passedFiltersSummary,
  };

  const html = buildAllocationSummaryReportHtml(options);

  const safeName = member.name.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '');
  const filename = `allocation-summary-${safeName || 'member'}.html`;

  outputReport(html, filename, getOutputFormat(params));
};
